import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { TranslocoDirective } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { AraButtonComponent } from '../ara-button/ara-button.component';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Icons } from '../../../util/Icons';
import { TrailingIconsService } from '@shared/services/trailing-icon.service';
import { AraInfoBadgeComponent } from '../ara-info-badge/ara-info-badge.component';
import { TableColumn } from '@shared/interfaces/table-column.interface';
import { DelayedSearchDirective } from '../../../directives/delayed-search.directive';
import { AraPaginatorComponent } from '../ara-paginator/ara-paginator.component';
import { AraPopoverComponent } from '../popover/popover.component';
import { TableFullscreenDialogComponent, TableFullscreenDialogData } from '../table-fullscreen-dialog/table-fullscreen-dialog.component';
import { TableState } from '../../../services/data/table-state';
import { TableCacheEntity } from '../../../services/data/TableCacheEntity';
import { AraReadModeDirective } from '../../../directives/ara-read-mode.directive';

import { AraCheckboxComponent } from '@shared/ui/ara-checkbox/ara-checkbox.component';
/**
 * Generic table shell with the same UI as the real-estates table:
 * card layout, search bar, fullscreen button, column filter, loading spinner,
 * reserved action-button area (gray bar) and ara-paginator.
 *
 * Table, action buttons and footer are passed in as `TemplateRef` inputs and rendered
 * here via `ngTemplateOutlet` (NOT via `<ng-content>`). Reason: only this way can the
 * same shell be reliably rendered a second time inside the fullscreen dialog —
 * projected `<ng-content>` content is not reliably stamped in the dialog overlay.
 *
 * Fullscreen: pass the outer template that wraps this shell as `fullscreenHostTemplate`
 * (plus a translated `dialogTitle`); the shell opens the fullscreen dialog itself.
 * The dialog renders the template with context `{ isInDialog: true }` — bind that to
 * `[isInDialog]` so the inner shell hides its own fullscreen button.
 *
 * `showSearch`, `showFullscreen`, `showColumnFilter`, `showPaginator` and `showRowActions`
 * default to `true` and can be disabled via a boolean input.
 *
 * Row reordering: the shell renders no rows itself (see above), so drag-and-drop stays entirely
 * in the caller's own template — apply `cdkDropList`/`cdkDrag`/`cdkDragHandle` there exactly as
 * for any other `mat-table`. Import `reorderItems()` from `./table-reorder.util` inside your
 * `(cdkDropListDropped)` handler to get the paging-aware splice + `sortOrder` recomputation for
 * free instead of re-deriving it per table (see `content-rule-sets.component.ts` for a reference
 * usage).
 *
 * Column lists: pass `columns` (labels = translation KEYS, translated by the shell) and use
 * `displayedColumns()` for the table's row defs — it returns the visible columns and appends
 * `'actions'` automatically while `showRowActions` is on (actions column is always last).
 *
 * Minimal usage:
 * ```html
 * <ng-template #host let-isInDialog="isInDialog">
 *   <ara-generic-table #shell (page)="load($event)" [columns]="columns" [tableTemplate]="tableTpl"
 *     [isInDialog]="isInDialog" [fullscreenHostTemplate]="host" [dialogTitle]="t('my.title')" ...>
 *   </ara-generic-table>
 *   <ng-template #tableTpl>
 *     <div class="table-scroll-body" araTableScrollBody>
 *       <table mat-table matSort [dataSource]="dataSource">
 *         <!-- own matColumnDef's ... -->
 *         <ng-container matColumnDef="actions">
 *           <th *matHeaderCellDef mat-header-cell></th>
 *           <td *matCellDef="let row" mat-cell>
 *             <ara-row-actions [row]="row" [menuTemplate]="menuTpl"></ara-row-actions>
 *           </td>
 *         </ng-container>
 *         <tr *matHeaderRowDef="shell.displayedColumns()" mat-header-row></tr>
 *         <tr *matRowDef="let row; columns: shell.displayedColumns()" mat-row></tr>
 *       </table>
 *     </div>
 *     <ng-template #menuTpl let-row><!-- mat-menu items --></ng-template>
 *   </ng-template>
 * </ng-template>
 * <ng-container *ngTemplateOutlet="host; context: { isInDialog: false }"></ng-container>
 * ```
 */
@Component({
	selector: 'ara-generic-table',
	templateUrl: './ara-generic-table.component.html',
	styleUrls: ['./ara-generic-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AraCheckboxComponent,
		TranslocoDirective,
		FormsModule,
		NgTemplateOutlet,
		MatIcon,
		MatInput,
		AraButtonComponent,
		MatMenu,
		MatMenuTrigger,
		MatProgressSpinner,
		DelayedSearchDirective,
		AraPaginatorComponent,
		AraPopoverComponent,
		AraInfoBadgeComponent,
	],
})
export class AraGenericTableComponent implements OnInit {
	protected readonly icons = inject(Icons);
	protected readonly trailingIconsService = inject(TrailingIconsService);
	private readonly dialog = inject(MatDialog);
	private readonly destroyRef = inject(DestroyRef);
	/** Present when the host carries the `araReadMode` directive → read-only table. */
	private readonly readModeDirective = inject(AraReadModeDirective, { optional: true, self: true });

	/**
	 * Read-only: hides only the EDIT/ADD affordances — the row-actions (three-dot) column and the
	 * gray action bar. Search, fullscreen, column filter and paginator stay. Off by default (only
	 * on when the host carries `araReadMode`), so existing tables are unaffected. The caller's own
	 * add row (`araTableAddRow`, which lives in its table template) should gate on `isReadMode()`.
	 */
	readonly isReadMode = computed(() => this.readModeDirective?.araReadMode() ?? false);

	/** Chrome flags after read mode: header actions + action bar + row actions are edit/add
	 *  affordances → hidden. Search, fullscreen, column filter and paginator stay. */
	protected readonly effShowActionBar = computed(() => this.showActionBar() && !this.isReadMode());
	protected readonly effShowRowActions = computed(() => this.showRowActions() && !this.isReadMode());

	// Content (as TemplateRef, rendered via ngTemplateOutlet)
	readonly tableTemplate = input<TemplateRef<unknown>>();
	/** Action buttons directly next to the search field (left side, e.g. clear-all-filters). */
	readonly searchActionsTemplate = input<TemplateRef<unknown>>();
	/** Action buttons in the top chrome bar (right-aligned, before the fullscreen/column-filter buttons). */
	readonly headerActionsTemplate = input<TemplateRef<unknown>>();
	/** Action buttons in the gray action bar between table and paginator (shown via `showActionBar`), e.g. an add button. */
	readonly actionBarTemplate = input<TemplateRef<unknown>>();
	/** Free content below the paginator (e.g. a status bar). */
	readonly footerTemplate = input<TemplateRef<unknown>>();

	// Fullscreen dialog (opened by the shell)
	/** Outer template wrapping this shell; rendered inside the fullscreen dialog with `{ isInDialog: true }`. */
	readonly fullscreenHostTemplate = input<TemplateRef<unknown>>();
	/** Already-translated dialog title. */
	readonly dialogTitle = input('');

	// Chrome toggles (visible by default)
	readonly showSearch = input(true);
	readonly showFullscreen = input(true);
	readonly showColumnFilter = input(true);
	readonly showPaginator = input(true);
	/** Central switch for the caller's actions column (three-dot menu). */
	readonly showRowActions = input(true);
	/** Sticky table header (and filter) rows — bind in the caller's header row defs:
	 * `*matHeaderRowDef="shell.displayedColumns(); sticky: shell.stickyHeader()"`. */
	readonly stickyHeader = input(true);
	/** Shows the gray action-button bar. */
	readonly showActionBar = input(false);
	/** Set when the table is rendered inside the fullscreen dialog — hides the fullscreen button. */
	readonly isInDialog = input(false);
	/** Marks the table as a beta feature — shows the BETA badge left of the fullscreen button. */
	readonly beta = input(false);

	// Columns (for the column filter menu). Note: the filter menu toggles `column.visible`
	// in place, so pass a component-owned array.
	readonly columns = input<TableColumn<unknown>[]>([]);

	// State
	readonly loading = input(false);

	/**
	 * Opt-in Zustands-Wiederherstellung: der Aufrufer erzeugt die `TableState` per `injectTableState()`
	 * und reicht sie hier herein. Die Shell persistiert Suche/Seite/Spalten automatisch; Sortierung und
	 * domänenspezifische Filter meldet der Aufrufer über `patchState()`. Ohne Wert bleibt die Tabelle
	 * wie bisher rein controlled. Restore-Eigentum steuert `autoRestore`; ausführlicher Contract:
	 * `docs/ui-state-persistence.md`.
	 */
	readonly tableState = input<TableState>();

	/**
	 * Wer besitzt das Wiederherstellen?
	 * - `true` (kanonisch, Default): Die Shell ruft in `ngOnInit` `restoreOrReset()` und emittiert den
	 *   Zustand einmalig über `stateRestored` (nicht in der Fullscreen-Dialog-Instanz). Der Aufrufer
	 *   stößt seinen initialen Load aus `(stateRestored)` an.
	 * - `false`: Der Aufrufer besitzt Restore/Load selbst (ruft `restoreOrReset()` und lädt in seinem
	 *   eigenen `ngOnInit`); die Shell persistiert weiterhin Suche/Seite/Spalten. Für bestehende Tabellen,
	 *   deren verzahnte Init-/Load-Reihenfolge das Restore selbst halten muss.
	 */
	readonly autoRestore = input(true);

	// Search
	readonly searchSubject = input<Subject<string>>();
	readonly searchValue = input('');
	readonly searchPlaceholder = input('common.searching');

	// Pagination
	readonly length = input(0);
	readonly pageIndex = input(0);
	readonly pageSize = input(10);
	readonly pageSizeOptions = input<number[]>([10, 20, 50, 100]);

	// Events
	readonly page = output<PageEvent>();
	readonly columnVisibilityChange = output<TableColumn<unknown>>();
	/** Einmalig beim Init emittierter, wiederhergestellter (bzw. auf Defaults gesetzter) Zustand. */
	readonly stateRestored = output<TableCacheEntity>();

	/** Embedded paginator — for client-side pagination assign `dataSource.paginator = shell.araPaginator().paginator()`. */
	readonly araPaginator = viewChild(AraPaginatorComponent);

	ngOnInit(): void {
		const state = this.tableState();
		if (!state) {
			return;
		}
		// Restore nur, wenn die Shell es besitzt (autoRestore) und nur auf der Inline-Instanz: die
		// Fullscreen-Dialog-Instanz teilt sich denselben Cache-Slot; ein erneutes restoreOrReset()
		// (ohne Navigation) würde die Filter zurücksetzen.
		if (this.autoRestore() && !this.isInDialog()) {
			this.stateRestored.emit(state.restoreOrReset());
		}
		// Debouncte Suche persistieren (die Shell rendert das Suchfeld selbst).
		this.searchSubject()
			?.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((search) => state.patch({ search }));
	}

	/**
	 * Visible column properties for the table's header/row defs, with the `'actions'`
	 * column appended automatically while `showRowActions` is on.
	 * Method (not computed) on purpose: callers mutate `column.visible` in place.
	 */
	displayedColumns(): string[] {
		const visible = this.columns()
			.filter((column) => column.visible)
			.map((column) => column.property);
		return this.effShowRowActions() ? [...visible, 'actions'] : visible;
	}

	/**
	 * Column names for a filter header row (`<property>Filter` per visible column),
	 * e.g. for per-column `ara-search-select` filters. `exclude` skips columns
	 * that have no filter (their `<property>Filter` matColumnDef does not exist).
	 */
	displayedFilterColumns(exclude: string[] = []): string[] {
		return this.columns()
			.filter((column) => column.visible && !exclude.includes(column.property))
			.map((column) => column.property + 'Filter');
	}

	openFullscreen(): void {
		const templateRef = this.fullscreenHostTemplate();
		if (!templateRef) {
			return;
		}
		this.dialog.open(TableFullscreenDialogComponent, {
			width: '95vw',
			height: '90vh',
			maxWidth: '95vw',
			panelClass: 'fullscreen-table-dialog',
			autoFocus: false,
			data: {
				templateRef,
				title: this.dialogTitle(),
			} satisfies TableFullscreenDialogData,
		});
	}

	toggleColumnVisibility(column: TableColumn<unknown>, event: Event): void {
		event.stopPropagation();
		event.stopImmediatePropagation();
		column.visible = !column.visible;
		this.tableState()?.captureColumns(this.columns());
		this.columnVisibilityChange.emit(column);
	}

	/** Paginierung persistieren und an den Aufrufer weiterreichen. */
	onPage(event: PageEvent): void {
		this.tableState()?.patch({ pageIndex: event.pageIndex, pageSize: event.pageSize });
		this.page.emit(event);
	}

	/**
	 * Zustand teilweise persistieren — für Dimensionen, die der Aufrufer besitzt: Sortierung (die
	 * `MatSort` liegt in seinem Template) und domänenspezifische Filter im `meta`-Bag.
	 */
	patchState(patch: Partial<TableCacheEntity>): void {
		this.tableState()?.patch(patch);
	}
}
